import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { AxiosResponse } from 'axios';

import { Modal, SingleSelect } from '@/common';
import { Button, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { ASSET_BY_ID_QUERY_KEY, DEFAULT_THUMBNAIL_EXTENSION, GET_EVENT_ASSETS } from '@/constants';
import { AssetType } from '@/enums';
import { BlobLocation } from '@/enums/BlobLocationEnum';
import { getAssetBlobClient, getAssetUrl, getThumbnailFromType, uploadAsset } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { RotateLeft, RotateRight } from '@/resources';
import { useCreateAssetMutation, useCreateSasTokenMutation, useUpdateAsset } from '@/services';
import { Asset, CreateEditAssetPayload, SelectOptionProps } from '@/types';

type CropAndFilterTypes = {
  asset: Asset;
  allEventAssets: Asset[];
  setEnableEdit: Dispatch<SetStateAction<boolean>>;
  closeModal: () => void;
};

const CropAndFilter = ({
  asset,
  allEventAssets,
  setEnableEdit,
  closeModal
}: CropAndFilterTypes) => {
  const [t] = useTranslation('common');
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isRotating, setIsRotating] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [filter, setFilter] = useState<SelectOptionProps>({ value: 'none', label: 'None' });
  const [isUploading, setIsUploading] = useState(false);
  const [isOpenChoiceModal, setIsOpenChoiceModal] = useState(false);

  const copiesCount = allEventAssets.filter((eventAsset) =>
    eventAsset.assetName
      .replace(/\s+/g, '')
      .includes(asset.assetName.replace(/\s+/g, '').split('.')[0])
  ).length;

  const filterOptions = [
    { value: 'none', label: t('none') },
    { value: 'grayscale(100%)', label: t('blackNWhite') },
    { value: 'sepia(100%)', label: t('sepia') }
  ];

  const { mutateAsync: createSasToken } = useCreateSasTokenMutation(queryClient, {
    onSuccess: () => {
      undefined;
    },
    onError: () => {
      showToast(ToastType.ERROR, t('sasError'));
    }
  });
  const { mutate: createAsset } = useCreateAssetMutation(queryClient, {
    onSuccess: (response?: AxiosResponse) => {
      setEnableEdit(false);
      closeModal();
      showToast(
        ToastType.SUCCESS,
        t('assetSuccessfullyCreated', { assetName: response?.data.assetName })
      );
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const { mutate: updateAsset } = useUpdateAsset(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_EVENT_ASSETS]);
      queryClient.invalidateQueries([ASSET_BY_ID_QUERY_KEY]);
      queryClient.removeQueries([ASSET_BY_ID_QUERY_KEY, { id: asset.id }]);
      queryClient.invalidateQueries();

      setEnableEdit(false);
      closeModal();
      showToast(ToastType.SUCCESS, t('assetSuccessfullyUpdated', { assetName: asset.assetName }));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const createPreviewCanvas = (rotate: number): HTMLCanvasElement | null => {
    const image = imgRef.current;

    if (!image) {
      return null;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    const normalizedRotate = rotate % 360;

    const angleInRadians = (normalizedRotate * Math.PI) / 180;
    const sin = Math.abs(Math.sin(angleInRadians));
    const cos = Math.abs(Math.cos(angleInRadians));

    const rotatedWidth = image.naturalWidth * cos + image.naturalHeight * sin;
    const rotatedHeight = image.naturalWidth * sin + image.naturalHeight * cos;

    canvas.width = Math.ceil(rotatedWidth);
    canvas.height = Math.ceil(rotatedHeight);

    ctx.save();

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(angleInRadians);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      -image.naturalWidth / 2,
      -image.naturalHeight / 2,
      image.naturalWidth,
      image.naturalHeight
    );

    ctx.restore();

    return canvas;
  };

  const createCanvas = (image: HTMLImageElement, filter?: string): string => {
    const format = 'image/png';
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return '';
    }

    const cropWidth = completedCrop?.width ?? image.naturalWidth;
    const cropHeight = completedCrop?.height ?? image.naturalHeight;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const cropX = (completedCrop ? completedCrop.x : 0) * scaleX;
    const cropY = (completedCrop ? completedCrop.y : 0) * scaleY;

    canvas.width = Math.ceil(cropWidth * scaleX);
    canvas.height = Math.ceil(cropHeight * scaleY);
    ctx.translate(-cropX, -cropY);

    ctx.save();

    ctx.filter = filter ?? '';
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight
    );

    ctx.restore();

    return canvas.toDataURL(format, 1.0) as string;
  };

  const updateImage = (rotate: number) => {
    const image = imgRef.current;
    if (!image || !completedCrop) {
      return;
    }

    const canvas = createPreviewCanvas(rotate);
    if (!canvas) {
      return;
    }
    const imageDataUrl = canvas.toDataURL('image/png', 1.0);
    setImageSrc(imageDataUrl);
  };

  const save = async (shouldSaveAsCopy: boolean) => {
    setIsUploading(true);

    const image = imgRef.current;
    if (!image) {
      return;
    }

    const createImageAsset = (src: string, format: string, fileName: string): Promise<File> => {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = src;

        image.onload = async () => {
          const blob = await fetch(image.src).then((response) => response.blob());
          const newAsset = new File([blob], fileName, {
            type: `image/${format}`
          });
          resolve(newAsset);
        };

        image.onerror = (error) => {
          reject(error);
        };
      });
    };

    try {
      const mainCanvasSrc = createCanvas(image, filter?.value as string);
      const thumbnailCanvasSrc = createCanvas(image, filter?.value as string);

      const originalExtension = asset.assetName.split('.').pop() || 'png';
      const fileFormat =
        originalExtension.toLowerCase() === 'jpg' ? 'jpeg' : originalExtension.toLowerCase();
      const fileExtension =
        originalExtension.toLowerCase() === 'jpeg' ? 'jpg' : originalExtension.toLowerCase();

      const assetName = shouldSaveAsCopy
        ? `${asset.assetName.split('.')[0]}_${copiesCount.toString()}`
        : asset.assetName.split('.')[0];
      const thumbnailName = shouldSaveAsCopy
        ? `${asset.assetName.split('.')[0]}_thumbnail_${copiesCount.toString()}`
        : `${asset.assetName.split('.')[0]}_thumbnail`;

      const assetFileName = shouldSaveAsCopy
        ? `copy_of_${asset.assetName.split('.')[0]}_${copiesCount.toString()}.${fileExtension}`
        : `${assetName}.${fileExtension}`;
      const thumbnailFileName = `${thumbnailName}.${fileExtension}`;

      const assetForUpload = await createImageAsset(
        mainCanvasSrc as string,
        fileFormat,
        assetFileName
      );
      const thumbnailForUpload = await createImageAsset(
        thumbnailCanvasSrc as string,
        fileFormat,
        thumbnailFileName
      );

      const thumbnail = await getThumbnailFromType(thumbnailForUpload);

      if (!assetForUpload || !thumbnail) {
        showToast(ToastType.ERROR, t('blobGenerationFailed'));
        return;
      }

      const sasTokenPayload = {
        id: asset.eventId,
        isPublic: false,
        isThumbnail: false,
        assetName: '',
        extension: `.${fileExtension}`,
        blobLocation: BlobLocation.EVENTS
      };

      const sasToken = await createSasToken(sasTokenPayload);
      if (!sasToken) {
        return;
      }

      const mainBlobClient = getAssetBlobClient(sasToken);
      const mainUpload = await uploadAsset(mainBlobClient, assetForUpload, setIsUploading);
      if (!mainUpload) {
        showToast(ToastType.ERROR, t('uploadFailed'));
        return;
      }

      const thumbnailSasTokenPayload = {
        id: asset.eventId,
        isPublic: false,
        isThumbnail: true,
        assetName: sasToken.data.assetName,
        extension: DEFAULT_THUMBNAIL_EXTENSION,
        blobLocation: BlobLocation.EVENTS
      };

      const thumbnailSasToken = await createSasToken(thumbnailSasTokenPayload);
      if (!thumbnailSasToken) {
        return;
      }

      const thumbnailBlobClient = getAssetBlobClient(thumbnailSasToken);
      const thumbnailUpload = await uploadAsset(
        thumbnailBlobClient,
        thumbnail.file,
        setIsUploading
      );
      if (!thumbnailUpload) {
        showToast(ToastType.ERROR, t('uploadFailed'));
        return;
      }

      const newAssetPayload: CreateEditAssetPayload = {
        eventId: Number(asset.eventId),
        assetType: AssetType.IMAGE,
        assetScope: 0,
        assetName: assetName.replace(/ /g, '_') + `.${fileExtension}`,
        assetUrl: getAssetUrl(sasToken),
        thumbnailUrl: getAssetUrl(thumbnailSasToken),
        assetSize: Number(500000),
        additionalData: asset.additionalData,
        tags: asset.tags,
        ...(shouldSaveAsCopy ? {} : { id: asset.id })
      };

      if (shouldSaveAsCopy) {
        createAsset(newAssetPayload);
      } else {
        updateAsset(newAssetPayload);
      }
    } catch (error) {
      showToast(ToastType.ERROR, t('assetError'));
      setIsUploading(false);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRotateClick = (rotate: number) => {
    setIsRotating(true);

    setTimeout(() => {
      updateImage(rotate);
      setIsRotating(false);
    }, 0);
  };

  const onLoadImage = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const fullCrop = (unit: string, width: number, height: number) => {
      return {
        unit: unit,
        x: 0,
        y: 0,
        width: width,
        height: height
      };
    };

    setCrop(fullCrop('%', 100, 100) as Crop);
    setCompletedCrop(fullCrop('px', width, height) as PixelCrop);
  };
  const openChoiceModal = () => {
    setIsOpenChoiceModal(true);
  };

  return (
    <div className='flex w-full flex-col'>
      {(isRotating || isUploading) && (
        <div className='absolute inset-0 z-10 flex items-center justify-center bg-black/50'>
          <Spinner />
        </div>
      )}
      <div className='flex max-h-[500px] justify-center'>
        {!!asset.assetUrl && (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={undefined}
            className='h-auto max-h-[400px] max-w-[300px] object-contain md:max-h-[500px] md:max-w-[1200px]'>
            <img
              ref={imgRef}
              src={imageSrc ?? asset.assetUrl}
              crossOrigin='anonymous'
              style={{
                filter: filter?.value as string
              }}
              onLoad={onLoadImage}
            />
          </ReactCrop>
        )}
      </div>
      <div className='flex flex-col'>
        <div className='my-2 flex flex-col justify-between md:flex-row'>
          <div className='flex flex-row'>
            <Label text={t('filter')} type={LABEL_TYPE.PARAGRAPH1} className='mr-4 text-black' />
            <SingleSelect
              id='filter'
              options={filterOptions}
              value={filter}
              handleChange={(value) => setFilter(value as SelectOptionProps)}
            />
          </div>
          <div className='mt-2 flex flex-row gap-2 md:mt-0'>
            <Label text={t('rotate')} type={LABEL_TYPE.PARAGRAPH1} className='mr-2 text-black' />
            <Button
              type={'button'}
              handleOnClick={() => handleRotateClick(-90)}
              variant={ButtonVariant.SECONDARY}>
              <RotateLeft />
            </Button>
            <Button
              type={'button'}
              handleOnClick={() => handleRotateClick(90)}
              variant={ButtonVariant.SECONDARY}>
              <RotateRight />
            </Button>
          </div>
        </div>
        <div className='flex w-full justify-end'>
          <Button
            type={'button'}
            variant={ButtonVariant.PRIMARY}
            handleOnClick={() => openChoiceModal()}
            className='mt-2'>
            {t('saveImage')}
          </Button>
        </div>
      </div>
      {isOpenChoiceModal && (
        <div className='fixed inset-0 z-20 flex items-center justify-center bg-black/50'>
          <Modal
            isOpen={isOpenChoiceModal}
            onClose={() => setIsOpenChoiceModal(false)}
            label={t('saveImage')}
            modalClasses='h-auto min-w-[320px]'>
            <div className='flex flex-col items-center justify-center gap-4 p-6'>
              <p className='text-center text-sm text-gray-600'>
                {t('replaceImage')} <b>{asset.assetName}</b>
                {'?'}
              </p>
              <div className='flex flex-row gap-3'>
                <Button
                  type={'button'}
                  variant={ButtonVariant.PRIMARY}
                  className='px-4'
                  handleOnClick={() => {
                    save(false);
                    setIsOpenChoiceModal(false);
                  }}>
                  {t('replaceOriginal')}
                </Button>
                <Button
                  type={'button'}
                  variant={ButtonVariant.SECONDARY}
                  className='px-4'
                  handleOnClick={() => {
                    save(true);
                    setIsOpenChoiceModal(false);
                  }}>
                  {t('saveAsNewCopy')}
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default CropAndFilter;
